import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { markUserNuxAsSeen } from '../../services/UserService';
import { Mixpanel } from '../../services/Mixpanel';
import { SEEN_NUX_KEYS } from '../../utils/users';
import { useDiffitUserContext } from '../../contexts/DiffitUserContext';

/**
 * Welcome modal shown when users first land on /workshop with defaultToUnlimited enabled.
 * Shows different messaging based on user type (free, trial, paid) and whether they're new or returning.
 */
export default function WelcomeToWorkshopModal({ showModal, setShowModal }) {
  const [error, setError] = useState(null);

  const {
    diffitUser,
    selectedOrg,
    hasActiveSubscription: isPaidUser,
    isFreeTrialUser: isTrialUser,
    refreshDiffitUser,
  } = useDiffitUserContext();

  const isNewUser = !diffitUser?.seen_nux_json?.welcome_to_diffit;
  const schoolName = selectedOrg?.org?.name;

  // Determine if this user should get a trial extension
  // New users already get a 14-day trial, so only extend for returning non-paid users
  const shouldExtendTrial = !isPaidUser && !isNewUser;

  const handleConfirm = async () => {
    setError(null);

    Mixpanel.track('Workshop Welcome Modal: Go to Diffit Clicked', {
      is_new_user: isNewUser,
      is_paid_user: isPaidUser,
      is_trial_user: isTrialUser,
      will_extend_trial: shouldExtendTrial,
    });

    try {
      // Close modal immediately for better UX
      setShowModal(false);

      await markUserNuxAsSeen(diffitUser.id, {
        [SEEN_NUX_KEYS.WORKSHOP_WELCOME_SEEN]: true,
      });

      // we extend their trial end date by 14 days, so we need to refresh the user
      refreshDiffitUser();
    } catch (err) {
      console.error('Error in workshop welcome modal:', err);
      setError('Something went wrong. Please try again.');
      setShowModal(true); // Re-open modal on error
      Mixpanel.track('Workshop Welcome Modal: Error', {
        error: err.message,
      });
    }
  };

  // Get the content based on user type
  // Check isPaidUser first since a user can be both paid and have trial flags
  const getContent = () => {
    // New paid users
    if (isPaidUser && isNewUser) {
      const orgName = schoolName || 'your school';
      const firstName = diffitUser?.given_name;
      const headline = firstName ? `Welcome, ${firstName}!` : 'Welcome to Diffit!';
      const message = orgName
        ? `You have access to the best of Diffit through ${orgName}'s license.`
        : "You have access to the best of Diffit through your school's license.";
      return { headline, message };
    }

    // Returning paid users
    if (isPaidUser) {
      return {
        headline: 'Welcome to the new Diffit!',
        message: (
          <>
            Get stations, math and science supports, sub plans, and more. Just tell Diffit what you
            need.
          </>
        ),
      };
    }

    // New trial users
    if (isTrialUser && isNewUser) {
      const firstName = diffitUser?.given_name;
      const headline = firstName ? `Welcome, ${firstName}!` : 'Welcome to Diffit!';
      return {
        headline,
        message: (
          <>
            You have a 14 day trial of premium features. Then, use the free version or explore
            plans.
          </>
        ),
      };
    }

    // Returning trial users
    if (isTrialUser) {
      return {
        headline: 'Premium access extended!',
        message: <>Try the latest from Diffit with 14 more days of unlimited access.</>,
      };
    }

    // Free users (trial ended) - this is the fallback
    return {
      headline: 'Premium access extended!',
      message: <>Try the latest from Diffit with 14 more days of unlimited access.</>,
    };
  };

  useEffect(() => {
    if (showModal) {
      Mixpanel.track('Workshop Welcome Modal: Shown', {
        is_new_user: isNewUser,
        is_paid_user: isPaidUser,
        is_trial_user: isTrialUser,
      });
    }
  }, [showModal]);

  const content = getContent();

  return (
    <Transition.Root show={showModal} as={Fragment}>
      <Dialog as='div' className='relative z-50' onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'>
          <div className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity' />
        </Transition.Child>

        <div className='fixed inset-0 z-10 overflow-y-auto'>
          <div className='flex min-h-full justify-center p-4 text-center items-center sm:p-0'>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
              enterTo='opacity-100 translate-y-0 sm:scale-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100 translate-y-0 sm:scale-100'
              leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'>
              <Dialog.Panel className='relative transform bg-white text-left shadow-xl transition-all sm:my-8 w-full sm:max-w-xl rounded-2xl px-8 py-8'>
                <div className='flex flex-col items-center gap-5'>
                  {/* Headline */}
                  <Dialog.Title as='h2' className='text-2xl font-bold text-gray-700 text-center'>
                    {content.headline}
                  </Dialog.Title>

                  {/* Message */}
                  <p className='text-gray-600 text-center'>{content.message}</p>

                  {/* Video */}
                  <div
                    className='relative w-full rounded-lg overflow-hidden'
                    style={{ paddingBottom: '56.25%' }}>
                    <iframe
                      className='absolute inset-0 w-full h-full border-0'
                      src='https://www.youtube.com/embed/dSiNluzmITM'
                      title='Welcome to Diffit'
                      allow='accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
                      allowFullScreen></iframe>
                  </div>

                  {/* Error message */}
                  {error && (
                    <div className='w-full p-3 bg-red-50 border border-red-200 rounded-lg'>
                      <p className='text-sm text-red-800 text-center'>{error}</p>
                    </div>
                  )}

                  {/* CTA Button */}
                  <button
                    type='button'
                    onClick={handleConfirm}
                    className='w-full bg-emerald-600 text-white font-semibold py-3 px-4 rounded-lg shadow-sm hover:bg-emerald-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-600 transition-colors'>
                    Get Started
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
